import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AddressData } from '@/shared/types/AddressData';
import { UserInspectorData } from '@/shared/types/UserData';
import { useErrorHandler } from '@/shared/hooks/useErrorHandler';

import { useAlertContext } from '@/contexts/alert.context';
import { inspectorUserService } from '@/services/inspectorUser.service';
import { cleanPhoneNumber } from '@/shared/utils/phoneHelpers';
import { useAuth } from '@/shared/hooks/useAuth';

export type CreateInspectorUserData = UserInspectorData &
  AddressData & {
    allowsDemandReceiving: boolean;
    allowsDirectAssignment: boolean;
    providerId?: string;
  };

export function useInspectorUserRegister() {
  const { notify } = useAlertContext();
  const { handleError } = useErrorHandler();
  const { user } = useAuth();

  const navigator = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data: CreateInspectorUserData) => {
    setIsSubmitting(true);
    try {
      const providerId = data.providerId || user?.providerId;

      if (!providerId) {
        throw new Error('Selecione uma prestadora para o inspetor');
      }

      const cleanPhone = cleanPhoneNumber(data.phone);

      await inspectorUserService.createInspectorUser({
        name: data.name,
        email: data.email,
        phoneNumber: cleanPhone,
        allowsDemandReceiving: data.allowsDemandReceiving ?? false,
        allowsDirectAssignment: data.allowsDirectAssignment ?? false,
        city: data.city,
        cpf: data.cpf.replace(/\D+/g, ''),
        district: data.district,
        integrationCode: data.integrationCode,
        number: String(data.number),
        postalCode: data.postalCode.replace('.', ''),
        providerId,
        state: data.state,
        street: data.street,
        complement: data.complement ?? '',
      });

      notify({ message: 'Usuário criado com sucesso!', type: 'success' });

      navigator('/inspector-list');
    } catch (error) {
      handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    onSubmit,
    isSubmitting,
  };
}
